import { action, flow, makeAutoObservable, makeObservable, observable, toJS } from "mobx";

import { makePersistable } from "mobx-persist-store";
import { apiFetcher } from "../utils/fetch";
import moment from "moment";

// TODO: can be discussed to move to utils/store
const createPagination = (limit = 10) => ({
  total: 0,
  page: 1,
  limit,
  pages: 0,
  hasNextPage: false,
  hasPrevPage: false,
});

const createCollection = ({ limit = 10, extra = {} } = {}) => ({
  list: [],
  pagination: createPagination(limit),
  selected: null,
  ...extra, // allows custom fields
});

export default class Subscription {
  // Latest Update
  selected = null;
  change = createCollection({
    extra: { pending: null },
  });
  invoice = createCollection({
    limit: 5,
    extra: { active: null },
  });
  payment = createCollection();
  meta = {
    plans: [],
    settings: null,
  };

  constructor() {
    makeAutoObservable(this);
    makePersistable(this, {
      name: "Subscription",
      properties: ["selected"],
      storage: typeof window !== "undefined" ? window.localStorage : undefined,
      // expireIn: 604800000, // 7 day in milliseconds to match refresh token expiry time
      // removeOnExpiration: true,
    });
  }

  // ==============> Actions <==============
  setSelected(value) {
    this.selected = value;
  }
  setCollectionList(key, list) {
    this[key].list = list;
  }
  setCollectionPagination(key, pagination) {
    this[key].pagination = {
      ...this[key].pagination,
      ...pagination,
    };
  }
  setCollectionSelected(key, value) {
    this[key].selected = value;
  }
  setActiveInvoice(invoice) {
    this.invoice.active = invoice;
  }
  setPendingChange(change) {
    this.change.pending = change;
  }
  get isTrialPlan() {
    return this.selected?.plan === "trial";
  }
  get isStarterPlan() {
    return this.selected?.plan === "starter";
  }
  get isFreePlan() {
    return this.selected?.plan === "trial" || this.selected?.plan === "starter";
  }
  get isSubscriptionExpired() {
    if (!this.selected?.period?.end) return false;
    return this.selected?.status === "expired" || moment(this.selected?.period?.end).isBefore(moment(), "day");
  }
  get willExpireSoon() {
    if (!this.selected?.period?.end) return false;
    const now = moment();
    const endDate = this.selected?.period?.end;
    return moment(endDate).isBetween(now, now.clone().add(7, "days"), undefined, "[]");
  }
  get isAccessExpired() {
    return this.isTrialExpired || this.isSubscriptionExpired;
  }

  // ==============> Generators <==============
  *fetchPlans() {
    try {
      const response = yield apiFetcher().get(`/subscriptions/plans`);
      const responseData = response?.data;
      this.meta.plans = responseData.data;
      return { status: response.status, data: responseData.data };
    } catch (error) {
      return {
        status: error?.response?.status,
        data: error?.response?.data,
      };
    }
  }

  *fetchSettings() {
    try {
      const response = yield apiFetcher().get(`/subscriptions/settings`);
      const responseData = response?.data;
      this.meta.settings = responseData.data;
      return { status: response.status, data: responseData.data };
    } catch (error) {
      return {
        status: error?.response?.status,
        data: error?.response?.data,
      };
    }
  }

  *validateDiscount(code, planCode) {
    try {
      const response = yield apiFetcher().get(`/subscriptions/discounts/validate?code=${code}&planCode=${planCode}`);
      return { status: response.status, data: response?.data?.data };
    } catch (error) {
      return {
        status: error?.response?.status,
        data: error?.response?.data,
      };
    }
  }

  // ----> Subscription <----
  *fetchByRealmId(realmId) {
    try {
      const { status, data } = yield apiFetcher().get(`/subscriptions?realmId=${realmId}`);
      const response = data?.data;
      this.setSelected(response.subscription);

      return { status, data: response.subscription };
    } catch (error) {
      console.error(error?.response?.data);
      return {
        status: error?.response?.status,
        data: error?.response?.data,
      };
    }
  }

  *fetchOne(id) {
    try {
      const { status, data } = yield apiFetcher().get(`/subscriptions/${id}`);
      const response = data?.data;

      this.setSelected(response.subscription);
      return { status, data: response.subscription };
    } catch (error) {
      console.error(error?.response?.data);
      return {
        status: error?.response?.status,
        data: error?.response?.data,
      };
    }
  }

  // ----> Subscription Changes <----
  *fetchChanges(id, params = {}) {
    try {
      const { page = 1, limit = 10, status = "all" } = params;
      const response = yield apiFetcher().get(
        `/subscriptions/${id}/changes?page=${page}&limit=${limit}&status=${status}`,
      );
      const responseData = response?.data;
      this.setCollectionList("change", responseData?.data);
      this.setCollectionPagination("change", responseData?.pagination);

      return { status: response.status, data: responseData };
    } catch (error) {
      console.error(error?.response?.data);
      return {
        status: error?.response?.status,
        data: error?.response?.data,
      };
    }
  }

  *fetchPendingChange(id, params = {}) {
    try {
      const { page = 1, limit = 10 } = params;
      const response = yield apiFetcher().get(`/subscriptions/${id}/changes?limit=${limit}`);
      const responseData = response?.data;
      this.setPendingChange(responseData?.data[0]);

      return { status: response.status, data: responseData.data[0] };
    } catch (error) {
      console.error(error?.response?.data);
      return {
        status: error?.response?.status,
        data: error?.response?.data,
      };
    }
  }

  *initiateChange(id, payload) {
    try {
      const { status, data } = yield apiFetcher().post(`/subscriptions/${id}/change`, payload);
      const transformedData = data.data;

      if (status === 200) {
        this.setPendingChange(transformedData.change);
        this.setActiveInvoice(transformedData.invoice);
      }

      return { status, data: transformedData };
    } catch (error) {
      console.error(error?.response?.data);
      return {
        status: error?.response?.status,
        data: error?.response?.data,
      };
    }
  }

  *cancelChange(subscriptionId, subscriptionChangeId) {
    try {
      const response = yield apiFetcher().post(
        `/subscriptions/${subscriptionId}/cancel-change/${subscriptionChangeId}`,
      );
      const responseData = response.data;

      this.setPendingChange(null);
      this.setActiveInvoice(null);

      return { status: response.status, data: responseData };
    } catch (error) {
      console.error(error?.response?.data);
      return {
        status: error?.response?.status,
        data: error?.response?.data,
      };
    }
  }

  // ----> Subscription Invoices <----
  *fetchInvoices(id, params = {}) {
    try {
      const { page = 1, limit = this.invoice.pagination.limit, status = "all" } = params;
      const response = yield apiFetcher().get(
        `/subscriptions/${id}/invoices?page=${page}&limit=${limit}&status=${status}`,
      );
      const responseData = response?.data;
      this.setCollectionList("invoice", responseData?.data);
      this.setCollectionPagination("invoice", responseData?.pagination);

      return { status: response.status, data: responseData };
    } catch (error) {
      console.error(error?.response?.data);
      return {
        status: error?.response?.status,
        data: error?.response?.data,
      };
    }
  }

  *fetchActiveInvoice(id, params = {}) {
    try {
      const { page = 1, limit = 1, changeIds = [], status = "open" } = params;
      const response = yield apiFetcher().get(
        `/subscriptions/${id}/invoices?changeIds=${changeIds?.join(",")}&status=${status}&limit=${limit}`,
      );
      // const response = yield apiFetcher().get(`/subscriptions/${id}/invoices?status=${status}&limit=${limit}`);

      const responseData = response?.data;
      const activeInvoice = responseData?.data?.length > 0 ? responseData?.data[0] : null;
      this.setActiveInvoice(activeInvoice);

      return { status: response.status, data: responseData.data[0] };
    } catch (error) {
      console.error(error?.response?.data);
      return {
        status: error?.response?.status,
        data: error?.response?.data,
      };
    }
  }

  *generateActiveInvoice(subscriptionId) {
    try {
      const { status, data } = yield apiFetcher().post(`/subscriptions/${subscriptionId}/renewal-invoice`);
      const responseData = data.data;
      this.setActiveInvoice(responseData.invoice);

      return { status, data: responseData };
    } catch (error) {
      console.error(error?.response?.data);
      return {
        status: error?.response?.status,
        data: error?.response?.data,
      };
    }
  }

  *regeneratePaymentLink(subscriptionId, invoiceId, customer = {}) {
    try {
      const { status, data } = yield apiFetcher().post(
        `/subscriptions/${subscriptionId}/invoices/${invoiceId}/regenerate-payment`,
        { customer },
      );
      const responseData = data.data;
      this.setActiveInvoice(responseData.invoice);

      return { status, data: responseData };
    } catch (error) {
      console.error(error?.response?.data);
      return {
        status: error?.response?.status,
        data: error?.response?.data,
      };
    }
  }

  // -----> Related Process <-----

  *applyInvoiceDiscount(subscriptionId, invoiceId, discountCode) {
    try {
      const { status, data } = yield apiFetcher().post(
        `/subscriptions/${subscriptionId}/invoices/${invoiceId}/discount`,
        { discountCode },
      );
      const responseData = data?.data;
      if (status === 200 && responseData) {
        this.setActiveInvoice(responseData);
      }
      return { status, data: responseData };
    } catch (error) {
      console.error(error?.response?.data);
      return {
        status: error?.response?.status,
        data: error?.response?.data,
      };
    }
  }

  *removeInvoiceDiscount(subscriptionId, invoiceId) {
    try {
      const { status, data } = yield apiFetcher().delete(
        `/subscriptions/${subscriptionId}/invoices/${invoiceId}/discount`,
      );
      const responseData = data?.data;
      if (status === 200 && responseData) {
        this.setActiveInvoice(responseData);
      }
      return { status, data: responseData };
    } catch (error) {
      console.error(error?.response?.data);
      return {
        status: error?.response?.status,
        data: error?.response?.data,
      };
    }
  }

  // -----> Delayed Disable <-----
  *updateDelayedDisable(subscriptionId, changeId, delayedDisable) {
    try {
      const { status, data } = yield apiFetcher().patch(
        `/subscriptions/${subscriptionId}/changes/${changeId}/delayed-disable`,
        { delayedDisable },
      );
      const responseData = data?.data;
      if (status === 200 && responseData?.change) {
        this.setPendingChange(responseData.change);
      }
      return { status, data: responseData };
    } catch (error) {
      console.error(error?.response?.data);
      return {
        status: error?.response?.status,
        data: error?.response?.data,
      };
    }
  }

  *updateRoomStatuses(id, rooms) {
    try {
      const { status, data } = yield apiFetcher().post(`/subscriptions/${id}/rooms`, { rooms });
      return { status, data: data.data };
    } catch (error) {
      console.error(error);
      return {
        status: error?.response?.status,
        data: error?.response?.data,
      };
    }
  }
}
